import React, { useState, useEffect } from 'react'
import { usePage, router, Link, Head } from '@inertiajs/react'
import TopBar from '@/components/TopBar'
import Sidebar from '@/components/Sidebar'
import { ToastProvider } from '@/components/ToastProvider'
import { AlertTriangle, XCircle, Info, X } from 'lucide-react'
import PropertyNotifications from '@/components/PropertyNotifications'
import FlashHandler from '@/components/FlashHandler'
import AnnouncementCard from '@/components/AnnouncementCard'
import SpotlightAnnouncement from '@/components/SpotlightAnnouncement'
import BillingAlertStrip from '@/components/BillingAlertStrip'
import { HotelPaletteContext } from '@/lib/hotel-palette'
import type {
  SyncStatus,
  AccountPropertyGroup,
  CurrentAccount,
  ReportingScope,
  AnnouncementData,
  BillingAlert,
} from '@/types/shared'

interface OperatorLayoutProps {
  currentUser: { id: number; email: string }
  // `basePath` is the backend-served canonical path for this property; every chrome link is that
  // plus a fixed suffix. `id` stays for the ActionCable subscription, which is a payload, not a URL.
  property: { id: number; basePath: string; name: string; currency: string; timezone: string }
  properties: AccountPropertyGroup[]
  /**
   * Tab title — the page's LEAF name only ("Prices", "Overrides"). This layout appends the
   * PROPERTY name and entrypoints/operator.tsx appends the brand, giving
   * "Prices · Holland Road · zewzew".
   *
   * The property name is the whole point on this layout. An operator running a dozen hotels
   * keeps several open at once, and every one of those tabs used to read an identical bare
   * brand — the page you wanted was findable only by clicking through them.
   *
   * REQUIRED on purpose: a new page that forgets fails `tsc --noEmit` rather than shipping
   * another indistinguishable tab.
   */
  title: string
  children: React.ReactNode
}

const SIDEBAR_COLLAPSED_KEY = 'crown-rms-sidebar-collapsed'

interface BannerItem {
  type: 'error' | 'warning' | 'info'
  message: string
  action?: { label: string; href: string }
  dismissable?: boolean
  // Stable key for session-local dismissal of server warnings (undefined = per-mount only)
  dismissKey?: string
  // Combined inventory: the other listing this fault belongs to, as the server names it. Absent for
  // the page's own listing.
  listing?: { name: string; role: string }
}

// Session-local dismissal: a dismissed transient warning stays hidden for this browser
// session (per property + warning kind), and reappears in a fresh session if unresolved.
function sessionDismissKey(propertyId: number | undefined, kind: string): string {
  return `crown-rms-warn-dismissed:${propertyId ?? 'na'}:${kind}`
}

function isSessionDismissed(key: string): boolean {
  if (typeof window === 'undefined') return false
  return window.sessionStorage.getItem(key) === 'true'
}

// Exported for its test only; the layout mounts it once, above the page.
export function WarningBanner() {
  const { props, url } = usePage<{ syncStatus?: SyncStatus | null; property?: { id: number; basePath: string } }>()
  const syncStatus = props.syncStatus
  const propertyId = props.property?.id
  // Backend-served; the banner's action link and the acknowledge PATCH are both this + a suffix.
  const propertyBasePath = props.property?.basePath
  const [dismissed, setDismissed] = useState<Set<number>>(new Set())
  const [, setSessionTick] = useState(0)

  // Don't show warnings during onboarding — the wizard handles its own flow
  if (url.includes('/onboarding')) return null

  const banners: BannerItem[] = []

  // ── Sync status banners — the server (SyncHealth) decides what to warn about; ──
  // ── this component renders its warning set verbatim. No client-side thresholds. ──
  if (syncStatus && Object.keys(syncStatus).length > 0) {
    // Structure changes keep their dedicated info banner (dismiss = acknowledge)
    if (propertyBasePath && syncStatus.structureChanges?.newRoomTypes?.length) {
      const names = syncStatus.structureChanges.newRoomTypes.join(', ')
      banners.push({
        type: 'info',
        message: `New room types detected: ${names}. Configure base rates to start computing prices.`,
        action: { label: 'Configure Now', href: `${propertyBasePath}/pricing-rules/rooms-setup` },
        dismissable: true,
      })
    }

    for (const warning of syncStatus.health?.warnings ?? []) {
      const dismissKey = warning.dismissable ? sessionDismissKey(propertyId, warning.kind) : undefined
      if (dismissKey && isSessionDismissed(dismissKey)) continue
      banners.push({
        type: warning.severity,
        message: warning.message,
        action: warning.action ?? undefined,
        dismissable: warning.dismissable,
        dismissKey,
      })
    }

    // Combined inventory: every OTHER listing's faults, named by the server. Pricing is managed at the
    // lead listing, but each listing syncs and publishes on its own, so a following listing's failed
    // push would otherwise show only on a page nobody opens. A dismissal is keyed by THAT listing, the
    // key its own page uses, so dismissing a fault here dismisses it there too.
    for (const listing of syncStatus.otherListings ?? []) {
      for (const warning of listing.warnings) {
        const dismissKey = warning.dismissable ? sessionDismissKey(listing.id, warning.kind) : undefined
        if (dismissKey && isSessionDismissed(dismissKey)) continue
        banners.push({
          type: warning.severity,
          message: warning.message,
          action: warning.action ?? undefined,
          dismissable: warning.dismissable,
          dismissKey,
          listing: { name: listing.name, role: listing.role },
        })
      }
    }
  }

  // ── Target comms deliberately do NOT appear here ───────────────────────────
  // This rail means "something is broken" — a failed push, a dead PMS connection, a room the
  // engine cannot price. Target steering commentary is not a fault: it is an ongoing readout of
  // a month, it re-derives every computation cycle, and it can never be resolved by acting on a
  // banner. Routing it here cost the rail its meaning — on 2026-07-27 Sachi showed five yellow
  // banners that were two facts about two months, with no month label to tell them apart,
  // because a title alone was all this component rendered. Target verdicts now live on the
  // month they describe, inside Target Outlook (dashboard) and the Targets page.
  const visibleBanners = banners.filter((_, i) => !dismissed.has(i))
  if (visibleBanners.length === 0) return null

  function handleDismiss(index: number) {
    const banner = banners[index]
    if (banner?.type === 'info' && propertyBasePath) {
      router.patch(`${propertyBasePath}/acknowledge-structure-changes`, {}, { preserveScroll: true })
    }
    if (banner?.dismissKey && typeof window !== 'undefined') {
      window.sessionStorage.setItem(banner.dismissKey, 'true')
      setSessionTick((t) => t + 1)
    }
    setDismissed((prev) => new Set(prev).add(index))
  }

  return (
    <div className="mb-4 space-y-2">
      {banners.map((banner, i) => {
        if (dismissed.has(i)) return null

        const isError = banner.type === 'error'
        const isInfo = banner.type === 'info'
        const Icon = isError ? XCircle : isInfo ? Info : AlertTriangle

        const className = isError
          ? 'flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-800'
          : isInfo
            ? 'flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm text-blue-800 animate-slide-in'
            : 'flex items-center gap-2 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-2.5 text-sm text-yellow-800'

        return (
          <div key={i} className={className}>
            <Icon className="h-4 w-4 shrink-0" />
            <span className="flex-1">
              {banner.listing && <ListingTag listing={banner.listing} tone={isError ? 'error' : 'warning'} />}
              {banner.message}
            </span>
            {banner.action && (
              <Link
                href={banner.action.href}
                className={
                  isInfo
                    ? 'ml-2 shrink-0 rounded-md bg-blue-100 px-3 py-1 text-xs font-medium text-blue-900 transition-colors hover:bg-blue-200'
                    : 'ml-2 shrink-0 rounded-md bg-yellow-100 px-3 py-1 text-xs font-medium text-yellow-900 transition-colors hover:bg-yellow-200'
                }
              >
                {banner.action.label}
              </Link>
            )}
            {banner.dismissable && (
              <button
                onClick={() => handleDismiss(i)}
                className={
                  isInfo
                    ? 'ml-1 shrink-0 rounded p-0.5 text-blue-600 transition-colors hover:bg-blue-200 hover:text-blue-900'
                    : 'ml-1 shrink-0 rounded p-0.5 text-yellow-600 transition-colors hover:bg-yellow-200 hover:text-yellow-900'
                }
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}

// Which listing of a combined inventory a fault belongs to: its name, then its role, in the row's own
// colour so it reads as part of the warning rather than a second alert. Inline, so on a narrow screen it
// wraps with the sentence it names.
function ListingTag({ listing, tone }: { listing: { name: string; role: string }; tone: 'error' | 'warning' }) {
  const className =
    tone === 'error'
      ? 'mr-2 inline-flex max-w-full items-baseline gap-1 rounded-md border border-red-200 bg-white/70 px-1.5 py-px align-baseline text-xs'
      : 'mr-2 inline-flex max-w-full items-baseline gap-1 rounded-md border border-yellow-200 bg-white/70 px-1.5 py-px align-baseline text-xs'
  return (
    <span className={className}>
      <span className="min-w-0 truncate font-semibold">{listing.name}</span>
      <span className={tone === 'error' ? 'shrink-0 text-red-700/80' : 'shrink-0 text-yellow-700/80'}>{listing.role}</span>
    </span>
  )
}

export default function OperatorLayout({ currentUser, property, properties, title, children }: OperatorLayoutProps) {
  // currentAccount is shared_props data (tenancy-14) — read from the page like syncStatus/dryRun
  // so every page that mounts this layout gets it without re-threading the prop.
  // reportingScope rides along for the same reason: it is shared_props, and the hotel palette has to
  // reach every HotelChip on the page — several of them nested deep inside grids and dropdowns —
  // without threading a members array through ten call sites. Ungrouped pages send null, which is
  // exactly the "no palette" case the context defaults to.
  // `announcement` rides along for the same reason — it is shared_props on every property-context
  // page (null elsewhere and once dismissed), so mounting the card ONCE here is what makes the news
  // reach an operator wherever they happen to be, without every page opting in.
  // `billingAlert` rides along for the same reason, and is the strongest case for it: money owed
  // must reach the operator wherever they are working, with zero per-page wiring. Null on a
  // healthy account, which the strip renders as nothing.
  // `spotlightAnnouncement` is a SECOND, independent candidate — see the mount below.
  const { syncStatus, currentAccount, reportingScope, announcement, spotlightAnnouncement, billingAlert } = usePage<{
    syncStatus?: SyncStatus | null
    currentAccount?: CurrentAccount | null
    reportingScope?: ReportingScope | null
    announcement?: AnnouncementData | null
    spotlightAnnouncement?: AnnouncementData | null
    billingAlert?: BillingAlert | null
  }>().props
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
  })

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(sidebarCollapsed))
  }, [sidebarCollapsed])

  return (
    <HotelPaletteContext.Provider value={reportingScope?.members ?? null}>
    <ToastProvider>
      {/* Leaf · property. The brand suffix is appended once, in entrypoints/operator.tsx.
          `property.name` is guarded because it arrives as a prop and a briefly-absent one
          must degrade to "Prices · zewzew", never "Prices · undefined". */}
      <Head title={property?.name ? `${title} · ${property.name}` : title} />
      <div className="flex h-screen flex-col">
        <div className="flex flex-1 overflow-hidden">
          <Sidebar
            basePath={property.basePath}
            accountBasePath={currentAccount?.basePath ?? null}
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            collapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          />
          {/* `relative` is the announcement card's positioning context: the card is absolute inside
              the CONTENT column, so it lands bottom-left of the content and stays clear of the
              sidebar whether it is expanded or collapsed — no magic offset to keep in step. It sits
              outside <main>, so it does not scroll away with the page. */}
          <div className="relative flex flex-1 flex-col overflow-hidden">
            {/* Top of the CONTENT column, above the header and outside <main>'s scroll area: the
                strip is visible on every page without scrolling, never covers the sidebar, and
                never lands on top of the header. */}
            <BillingAlertStrip alert={billingAlert} />
            <TopBar
              currentUser={currentUser}
              property={property}
              properties={properties}
              currentAccount={currentAccount ?? null}
              onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
              syncStatus={syncStatus}
            />
            <main className="flex-1 overflow-y-auto bg-background">
              <div className="animate-fade-in-up px-4 py-6 sm:px-6 lg:px-8">
                <FlashHandler />
                <PropertyNotifications propertyId={property.id} />
                <WarningBanner />
                {children}
              </div>
            </main>
            {/* At most one card, ever (decision 3: newest only, never queued). `key` resets the
                card's local dismissed state if a different announcement arrives. */}
            {announcement && <AnnouncementCard key={announcement.id} announcement={announcement} />}
          </div>
          {/* NO CONDITIONAL BETWEEN THE TWO: treatments never suppress each other (§13.2). A
              pending Spotlight and a pending card both render — the Spotlight's z-40 backdrop
              overlays the card's z-30, and closing it reveals the card beneath. Fixed to the
              viewport, so it sits outside the content column the card is anchored to. */}
          {spotlightAnnouncement && (
            <SpotlightAnnouncement key={spotlightAnnouncement.id} announcement={spotlightAnnouncement} />
          )}
        </div>
      </div>
    </ToastProvider>
    </HotelPaletteContext.Provider>
  )
}
